import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useLayoutStore } from '../../state/layout.store'
import React, { useCallback, useMemo } from 'react'
import { useFormik } from 'formik'
import useSWR from 'swr'
import { trackerAPIFetcher, urlCarriers } from '../../libraries/tracker.api'
import { capitalize } from '../../libraries/utils'
import { useTranslation } from 'react-i18next'
import PackageModel, { ExtraData, validRegexId } from '@geartrack/shared-lib/dist/PackageModel'
import usePackagesRepository from '../../hooks/usePackagesRepository'
import { usePackages } from '../PackagesProvider'
import { packageValidationSchema } from '../../utils/packageValidation'

const AddPackageModal = React.memo(() => {
  const isVisible = useLayoutStore(state => state.packageModalIsVisible)
  const packageToEdit = useLayoutStore(state => state.packageModalEditPackage)
  const handleClose = useLayoutStore(state => state.packageModalClose)

  if (!isVisible) {
    // performance improvement
    return null
  }

  return (
    <Modal size="lg" show={true} onHide={handleClose}>
      <FormPackage packageToEdit={packageToEdit} handleModalClose={handleClose} />
    </Modal>
  )
})

const validationSchema = (editingPackage: string | null, t, packages: PackageModel[]) => {
  return packageValidationSchema({
    t,
    extendTrackingId: trackingId =>
      trackingId.test('is-trackingid-unique', t('trackingId.unique', { ns: 'validation' }), value => {
        if (editingPackage !== null && editingPackage === value) {
          return true
        }

        return packages.filter(p => p.trackingId === value).length === 0
      }),
  })
}

const FormPackage = ({ packageToEdit, handleModalClose }) => {
  const pkgRepo = usePackagesRepository()
  const closeModal = useLayoutStore(state => state.packageModalClose)
  const startId = useLayoutStore(state => state.packageModalStartPackage)
  const packages = usePackages()
  const { t } = useTranslation('packages')

  const initialValues = useMemo(() => {
    const tmp = {
      trackingId: packageToEdit ? packageToEdit.trackingId : startId ? startId : '',
      description: packageToEdit ? packageToEdit.description ?? '' : '',
    }

    if (packageToEdit && packageToEdit.extraData) {
      for (let extraDataKey in packageToEdit.extraData) {
        tmp[extraDataKey] = packageToEdit.extraData[extraDataKey]
      }
    }

    return tmp
  }, [packageToEdit, startId])

  const onSubmit = useCallback(
    async values => {
      const extraData = Object.fromEntries(
        Object.entries(values)
          .filter(([key]) => !['trackingId', 'description'].includes(key))
          .map(([key, value]) => {
            let v = value
            if (typeof v === 'string') {
              v = v.trim()
            }
            return [key, v]
          })
      ) as ExtraData

      if (packageToEdit) {
        // editing a package
        pkgRepo.updatePackage(packageToEdit, {
          description: capitalize(values.description?.trim()),
          extraData,
        })
      } else {
        // new package
        const newPackage: PackageModel = PackageModel.create(
          values.trackingId?.trim(),
          capitalize(values.description?.trim()),
          extraData
        )

        pkgRepo.addPackage(newPackage)
      }

      closeModal()
    },
    [closeModal, packageToEdit, pkgRepo]
  )

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema: validationSchema(packageToEdit?.trackingId, t, packages),
  })

  const isValidId = Boolean(!formik.errors.trackingId && validRegexId.test(formik.values.trackingId))

  const { data, error } = useSWR(isValidId ? urlCarriers(formik.values.trackingId?.trim()) : null, trackerAPIFetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  })

  return (
    <Form noValidate onSubmit={formik.handleSubmit}>
      <Modal.Header closeButton>
        <Modal.Title>
          {packageToEdit ? t('add-package-modal.edit-title') : t('add-package-modal.add-title')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!packageToEdit && ( // hide desc when editing
          <Description data={data} error={error} id={formik.values.trackingId?.trim()} isValidId={isValidId} t={t} />
        )}

        {!packageToEdit && ( // hide tracking id field when editing
          <Form.Group controlId="trackingId" className="mb-3">
            <Form.Label className="font-weight-bold">
              Tracking ID <span className="text-danger">*</span>{' '}
            </Form.Label>
            <Form.Control
              type="text"
              name="trackingId"
              placeholder="LZ144795942CN"
              value={formik.values.trackingId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.errors.trackingId}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.trackingId as any}</Form.Control.Feedback>
            <Form.Text className="text-muted">{t('add-package-modal.tracking id help')}</Form.Text>
          </Form.Group>
        )}

        {data &&
          Object.entries(necessaryParamsFromCarriersList(data)).map(([fieldName, carrierNames], i) => (
            <Form.Group controlId={fieldName} key={i} className="mb-3">
              <Form.Label className="font-weight-bold">
                {/* @ts-ignore */}
                {t(`order.extraFields.${fieldName}.name`)}
              </Form.Label>
              <Form.Control
                type="text"
                name={fieldName}
                //@ts-ignore
                placeholder={t(`order.extraFields.${fieldName}.placeholder`)}
                value={formik.values[fieldName] ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors[fieldName]}
              />
              <Form.Control.Feedback type="invalid">{formik.errors[fieldName] as any}</Form.Control.Feedback>
              <Form.Text className="text-muted">
                {t('add-package-modal.extraData.begin')}
                <strong className="ms-1">{carrierNames.join(', ')}</strong>. {t('add-package-modal.extraData.end')}
              </Form.Text>
            </Form.Group>
          ))}

        <Form.Group controlId="description">
          <Form.Label className="font-weight-bold">{t('add-package-modal.description.title')}</Form.Label>
          <Form.Control
            type="text"
            name="description"
            placeholder={t('add-package-modal.description.placeholder')}
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isInvalid={!!formik.errors.description}
          />
          <Form.Control.Feedback type="invalid">{formik.errors.description as any}</Form.Control.Feedback>
          <Form.Text className="text-muted">{t('add-package-modal.description.help')}</Form.Text>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose}>
          {t('add-package-modal.close')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={(!startId && !formik.dirty) || !formik.isValid || formik.isSubmitting}
        >
          {formik.isSubmitting ? (
            <Spinner animation="border" size="sm" as="span" role="status" />
          ) : packageToEdit ? (
            t('add-package-modal.edit button')
          ) : (
            t('add-package-modal.add button')
          )}
        </Button>
      </Modal.Footer>
    </Form>
  )
}

const Description = ({ data, error, id, isValidId, t }) => {
  if (!id || error || !isValidId) {
    return <p className="mb-3">{t('add-package-modal.feedback.empty id')}</p>
  }

  if (!data) {
    return (
      <p className="mb-3">
        <i className="fas fa-circle-notch fa-spin" />
      </p>
    )
  }

  if (data.carriers.length === 0) {
    return <p className="mb-3">{t('add-package-modal.feedback.no compatible carriers')}</p>
  }

  return (
    <p className="mb-3">
      {data.carriers.length} {t('add-package-modal.feedback.carrier', { count: data.carriers.length })}{' '}
      {t('add-package-modal.feedback.compatible', { count: data.carriers.length })}{' '}
      <strong>{data.carriers.map(c => c.fullName).join(', ')}</strong>.
    </p>
  )
}

export const necessaryParamsFromCarriersList = (res): { [index: string]: string[] } => {
  const data: { [index: string]: string[] } = {}

  for (let carrier of res.carriers) {
    for (let i = 0; i < carrier.necessaryParams.length; i++) {
      const field = carrier.necessaryParams[i].name
      if (!data[field]) {
        data[field] = [carrier.fullName]
      } else {
        data[field].push(carrier.fullName)
      }
    }
  }

  return data
}

export default AddPackageModal
